// PURE decoders (MAPFIRST R12): bytes / text in → plain objects + typed arrays
// out. No three, no DOM — this module is what a worker executor imports
// later; the loaders (ways2.js, trees.js, shrubs.js) keep only the
// main-thread BUILD half. (The structures.bin decode is the pool's since R8c2:
// client/src/pool-structures.js.) Until the worker exists the decoders run
// on the main thread, and every multi-stage decoder awaits `slice()`
// (sched.js) between its stages so the 2D Map keeps getting frames.
//
// The long ways passes — drawnRibbons, forkDraw, paintParts, laneMarks
// (game/waysdraw.js) — are the BAKE's since R0 WAY1 (the waytiles step:
// client/src/waybuild.js -> way1/ tiles; they were 1.3-1.45 s of main thread
// on a desktop, sliced into step generators here before). What is left is
// sequential loops too: each a STEP GENERATOR (parseSteps, graphSteps,
// loadSteps) that yields, and runSteps below runs steps until the slice is
// due, then awaits slice(). The same code in the same order as the one-shot
// function (drain) — the same bytes; scripts/check-mapfirst.mjs proves every
// decoder here equals the one-shot code, on the live bake. (The paved ground
// is no client decode: the GROUND-M bake carries the pave carve.)
import { checkWays, graphSteps } from '../../game/waysdraw.js';
import { slice as defaultSlice, act, due } from './sched.js';

// a step generator run on the main thread: steps until the slice is due
// (sched.js due: the phase budget), then `await slice()`; each task's work
// under ONE long-task label. -> the generator's return value
export async function runSteps(it, label, slice = defaultSlice) {
  for (;;) {
    const r = act(label, () => {
      let s;
      do s = it.next(); while (!s.done && !due());
      return s;
    });
    if (r.done) return r.value;
    await slice();
  }
}

// JSON.parse IN STEPS (ways.json is 32 MB: one JSON.parse is a 200-400 ms
// task on a desktop). parseSteps(text) returns the SAME value as
// JSON.parse(text): a big array / object (one that does not close within
// `chunk` chars) is split at its own top-level commas — a scanner that jumps
// between the structural characters, skips strings (escapes included) and
// counts brackets — and its members are parsed by JSON.parse in groups of
// about `chunk` chars ('[' + elements + ']', '{' + members + '}'), a big
// member recursively; a step per group. Object members are defined in source
// order like JSON.parse (CreateDataProperty: a "__proto__" key is an own
// property; a repeated key keeps its first place, the last value). Malformed
// text throws a SyntaxError (a group's JSON.parse or the scanner).
// check-mapfirst pins parseSteps == JSON.parse on the live ways.json and on
// edge fixtures (every path at tiny chunks).
const _def = (o, k, v) => {
  if (k === '__proto__') Object.defineProperty(o, k, { value: v, writable: true, enumerable: true, configurable: true });
  else o[k] = v;
};
export function* parseSteps(text, chunk = 1 << 18) {
  const n = text.length;
  const SR = /["[\]{}]/g;                       // the structural characters
  const ws = (c) => c === 32 || c === 10 || c === 13 || c === 9;
  const skipWs = (i) => { while (i < n && ws(text.charCodeAt(i))) i++; return i; };
  const bad = (i) => { throw new SyntaxError(`JSON (sliced): unexpected input at position ${i}`); };
  // the end (exclusive) of the string starting at i (a '"')
  const strEnd = (i) => {
    for (let j = i + 1; ;) {
      const q = text.indexOf('"', j);
      if (q < 0) bad(i);
      let b = q - 1;
      while (text.charCodeAt(b) === 92) b--;     // backslashes before the quote
      if ((q - 1 - b) % 2 === 0) return q + 1;  // an even run: the quote closes
      j = q + 1;
    }
  };
  // the end (exclusive) of the value starting at i (no leading whitespace)
  // when it ends within `lim` chars, else -1 (a container only: a string or
  // a literal always gets its end)
  const endWithin = (i, lim) => {
    const c = text.charCodeAt(i);
    if (c === 34) return strEnd(i);
    if (c === 91 || c === 123) {
      const stop = i + lim;
      let d = 0;
      SR.lastIndex = i;
      while (SR.test(text)) {
        const j = SR.lastIndex - 1;
        if (j >= stop) return -1;
        const k = text.charCodeAt(j);
        if (k === 34) SR.lastIndex = strEnd(j);
        else if (k === 91 || k === 123) d++;
        else if (--d === 0) return j + 1;
      }
      return bad(i);                             // never closes
    }
    let j = i;
    while (j < n) { const k = text.charCodeAt(j); if (k === 44 || k === 93 || k === 125 || ws(k)) break; j++; }
    return j > i ? j : bad(i);
  };
  // the big container starting at a -> [value, its end]
  function* span(a) {
    const arr = text.charCodeAt(a) === 91, close = arr ? 93 : 125, out = arr ? [] : {};
    let i = skipWs(a + 1), g0 = -1, g1 = -1;
    // a group of small members [g0, g1) in one JSON.parse
    const flush = () => {
      if (g0 < 0) return;
      const part = JSON.parse((arr ? '[' : '{') + text.slice(g0, g1) + (arr ? ']' : '}'));
      if (arr) for (let k = 0; k < part.length; k++) out.push(part[k]);
      else for (const k of Object.keys(part)) _def(out, k, part[k]);
      g0 = g1 = -1;
    };
    if (text.charCodeAt(i) === close) return [out, i + 1];
    for (;;) {
      const m0 = i;
      let v0 = i, key = null;
      if (!arr) {
        if (text.charCodeAt(i) !== 34) bad(i);
        const k1 = strEnd(i);
        i = skipWs(k1);
        if (text.charCodeAt(i) !== 58) bad(i);
        v0 = i = skipWs(i + 1);
        key = [m0, k1];
      }
      let v1 = endWithin(v0, chunk);
      if (v1 < 0) {
        flush();
        yield;
        const [v, e] = yield* span(v0);
        v1 = e;
        if (arr) out.push(v); else _def(out, JSON.parse(text.slice(key[0], key[1])), v);
      } else {
        if (g0 < 0) g0 = m0;
        g1 = v1;
        if (g1 - g0 >= chunk) { flush(); yield; }
      }
      i = skipWs(v1);
      const c = text.charCodeAt(i);
      if (c === 44) { i = skipWs(i + 1); continue; }
      if (c === close) { flush(); return [out, i + 1]; }
      bad(i);
    }
  }
  const a = skipWs(0);
  const c = a < n ? text.charCodeAt(a) : -1;
  // a small or scalar document, or a blank one: JSON.parse itself (its errors)
  if ((c !== 91 && c !== 123) || endWithin(a, chunk) >= 0) return JSON.parse(text);
  const [v, e] = yield* span(a);
  if (skipWs(e) !== n) bad(e);
  return v;
}

// the slim ways doc's text (ways_slim.json, game/way1.js SLIM: the readers'
// fields of ways.json) → {doc, gin}: parse, check, the graph input
// (o.slice: the slicer; o.onGraph(doc, gin): the Map's early hook, awaited
// when it returns a promise, must not mutate the doc). Each stage runs under its long-task label (sched.js act:
// `ways.<stage>`, the load report's lt_top / lt_by). Throws what checkWays
// throws. The meshes are the bake's way1/ tiles (ways2.js _upload).
export async function decodeWays(text, file = 'ways_slim.json', o = {}) {
  const slice = o.slice || defaultSlice;
  const doc = typeof text === 'string' ? await runSteps(parseSteps(text), 'ways.parse', slice) : text;
  await slice();
  act('ways.check', () => checkWays(doc, file));
  await slice();
  const gin = await runSteps(graphSteps(doc), 'ways.graph', slice);
  if (o.onGraph) await act('ways.mapdata', () => o.onGraph(doc, gin));
  return { doc, gin };
}
